import { createRouter, createWebHistory } from "vue-router";
import { keycloakAuthService } from "@/infrastructure/adapters/KeycloakAuthService";
import { logger } from "@/infrastructure/services/LoggerService";
import { clearOrganizationCache } from "@/shared/utils/authCleanup";
import { initializeTheme } from "@/shared/composables/usePreferences";
import { postHogBOService } from "@/infrastructure/services/PostHogBOService";
import { isBackOfficeRoute } from "@/shared/utils/routeUtils";
import { useToast } from "@/shared/composables/useToast";
import i18n from "@/plugins/i18n";
// Route components
import Onboarding from "@/presentation/views/Onboarding.vue";
import OnboardingSuccess from "@/presentation/views/OnboardingSuccess.vue";
import Logout from "@/presentation/views/Logout.vue";
import NotFound from "@/presentation/views/NotFound.vue";
import Error from "@/presentation/views/Error.vue";
import Dashboard from "@/presentation/views/Dashboard.vue";
import ConfirmOrganization from "@/presentation/views/ConfirmOrganization.vue";
// Dashboard child components (moved to components as they're not standalone views)
import Transactions from "@/presentation/components/dashboard/Transactions.vue";
import Subscriptions from "@/presentation/components/dashboard/Subscriptions.vue";
import Invoices from "@/presentation/components/dashboard/Invoices.vue";
import Parameters from "@/presentation/components/dashboard/Parameters.vue";
// Export routes array so it can be imported without importing the router instance
// This prevents circular dependency issues when other modules need to check route metadata
export const routes = [
    {
        path: "/",
        redirect: "/dashboard",
    },
    {
        path: "/dashboard",
        name: "Dashboard",
        component: Dashboard,
        meta: { requiresAuth: true },
        children: [
            {
                path: "",
                name: "DashboardHome",
                redirect: { name: "DashboardTransactions" },
            },
            {
                path: "transactions",
                name: "DashboardTransactions",
                component: Transactions,
                meta: { requiresAuth: true },
            },
            {
                path: "subscriptions",
                name: "DashboardSubscriptions",
                component: Subscriptions,
                meta: { requiresAuth: true },
            },
            {
                path: "invoices",
                name: "DashboardInvoices",
                component: Invoices,
                meta: { requiresAuth: true },
            },
            {
                path: "parameters",
                name: "DashboardParameters",
                component: Parameters,
                meta: { requiresAuth: true },
            },
        ],
    },
    {
        path: "/session-ended",
        name: "SessionEnded",
        component: Logout,
        meta: { requiresAuth: false, isLoginPage: true },
    },
    {
        path: "/onboarding",
        name: "Onboarding",
        component: Onboarding,
        meta: { requiresAuth: true, isOnboardingPage: true },
    },
    {
        path: "/onboarding/success",
        name: "OnboardingSuccess",
        component: OnboardingSuccess,
        meta: { requiresAuth: true, isOnboardingPage: true },
    },
    {
        path: "/error",
        name: "Error",
        component: Error,
        meta: { requiresAuth: false, isPublicPage: true },
    },
    {
        path: "/comfirm/:organizationId",
        name: "ConfirmOrganization",
        component: ConfirmOrganization,
        meta: { requiresAuth: false, isPublicPage: true },
    },
    // 404 handler
    {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: NotFound,
        meta: { requiresAuth: false, isPublicPage: true },
    },
];
const router = createRouter({
    history: createWebHistory(),
    routes,
});
/**
 * Builds a URL path preserving query parameters.
 * This is used when we need to clean up URL hash fragments while keeping the current query string.
 */
function buildPathWithQuery(path, query) {
    if (!query || Object.keys(query).length === 0) {
        return path;
    }
    const search = new URLSearchParams(query).toString();
    return `${path}?${search}`;
}
function withoutHash(fullPath) {
    return fullPath?.split("#")[0];
}
function isAuthRedirectTarget(to, redirectPath) {
    return [
        withoutHash(to.fullPath),
        to.path,
        withoutHash(to.redirectedFrom?.fullPath),
        to.redirectedFrom?.path,
    ].includes(redirectPath);
}
// Helper function to wait for authentication service initialization with timeout
function waitForAuthInitialization() {
    return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max wait
        const checkInitialization = () => {
            if (keycloakAuthService.isInitialized()) {
                resolve();
            }
            else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkInitialization, 100); // Check every 100ms
            }
            else {
                logger.debug("Authentication service initialization timeout");
                resolve(); // Resolve anyway to avoid blocking
            }
        };
        checkInitialization();
    });
}
// Route guard
router.beforeEach(async (to, from, next) => {
    logger.debug(`Navigation: ${from.path} -> ${to.path}`);
    logger.debug("Auth state:", {
        isInitialized: keycloakAuthService.isInitialized(),
        isAuthenticated: keycloakAuthService.isAuthenticated(),
    });
    // Check if we're navigating to error page with explicit intent to stay
    const stayOnErrorPage = sessionStorage.getItem("stay-on-error-page");
    if (to.path === "/error" && stayOnErrorPage) {
        logger.debug("Navigating to error page with explicit intent to stay");
        next();
        return;
    }
    try {
        // Check if we have Keycloak error in URL hash (like #error=login_required)
        // but only if user is not already authenticated and not navigating to error page
        const hash = window.location.hash;
        if (hash.includes("error=login_required") &&
            !keycloakAuthService.isAuthenticated() &&
            to.path !== "/error") {
            logger.debug("Keycloak login_required error detected in URL for unauthenticated user");
            // If we're not already on login page AND not going to public page
            if (to.path !== "/session-ended" && !to.meta?.isPublicPage) {
                logger.debug("Login required error - saving target path and starting authentication");
                // Wait for auth service to be initialized before attempting login
                logger.debug("Waiting for auth service initialization...");
                await waitForAuthInitialization();
                // Save target path for redirect after login (without hash parameters)
                const cleanPath = to.path +
                    (to.query && Object.keys(to.query).length > 0
                        ? "?" + new URLSearchParams(to.query).toString()
                        : "");
                sessionStorage.setItem("auth-redirect-path", cleanPath);
                // Create the full redirect URI to preserve the intended destination (without hash parameters)
                const currentUrl = window.location.origin + cleanPath;
                logger.debug("Starting login with return URL due to login_required error:", currentUrl);
                // OP-5119: drop the cached org so the forced login goes out with the
                // plain `organization` scope. The silent SSO that just failed used
                // `organization:<cachedOrg>` — if we redirected with that same scope,
                // a user signing in with credentials that don't belong to the cached
                // org would loop or be assigned the wrong org.
                clearOrganizationCache();
                // Start login process with the intended destination
                try {
                    await keycloakAuthService.login({ redirectUri: currentUrl });
                    // Login will redirect, so we don't need to call next()
                    return;
                }
                catch (error) {
                    logger.debug("Failed to trigger login on login_required error:", error);
                    // Fallback: redirect to login page
                    next({ path: "/session-ended" });
                    return;
                }
            }
        }
        // Clean up Keycloak parameters from URL hash if user is authenticated
        if (keycloakAuthService.isAuthenticated() &&
            hash &&
            (hash.includes("error=") || hash.includes("code=") || hash.includes("state="))) {
            logger.debug("User is authenticated, cleaning up Keycloak parameters from URL");
            const cleanPath = buildPathWithQuery(to.path, to.query);
            window.history.replaceState({}, document.title, cleanPath);
        }
        // Special handling for public pages - always allow access without authentication
        if (to.meta?.isPublicPage) {
            logger.debug("Navigating to public page, no authentication required");
            next();
            return;
        }
        // Special handling for login page - always allow access but check auth state
        if (to.meta?.isLoginPage) {
            logger.debug("Navigating to login page");
            // Wait for authentication service initialization
            await waitForAuthInitialization();
            // If user is authenticated, redirect to home instead of showing login page
            // BUT only if there's no error in the URL (user might have been logged out due to session expiry)
            if (keycloakAuthService.isAuthenticated() && !hash.includes("error=")) {
                logger.debug("User is authenticated, redirecting from login page to home");
                next({ path: "/dashboard" });
                return;
            }
            // Clear force logout flag to allow login from login page
            sessionStorage.removeItem("force-logout");
            next();
            return;
        }
        // Check if user just logged out and we should not auto-authenticate
        const forceLogout = sessionStorage.getItem("force-logout");
        if (forceLogout) {
            const logoutTime = parseInt(forceLogout);
            const currentTime = Date.now();
            const timeDiff = currentTime - logoutTime;
            if (timeDiff < 5 * 60 * 1000) {
                // Don't interfere if there's a login_required error - user explicitly needs to authenticate
                const hash = window.location.hash;
                if (hash.includes("error=login_required")) {
                    logger.debug("Recent logout detected but login_required error present, clearing logout flag and allowing authentication");
                    sessionStorage.removeItem("force-logout");
                    // Continue with normal flow - let the login_required logic handle it
                }
                else {
                    logger.debug("Recent logout detected, waiting for auth initialization before checking state...");
                    // Wait for auth service to be initialized before making decisions
                    await waitForAuthInitialization();
                    if (keycloakAuthService.isAuthenticated()) {
                        logger.debug("User is authenticated despite recent logout, clearing logout flag");
                        sessionStorage.removeItem("force-logout");
                        // Continue with normal navigation logic
                    }
                    else {
                        logger.debug("User not authenticated and recent logout detected, checking navigation type");
                        // Clear force-logout flag when navigating to or from login page
                        if (from.path === "/session-ended" ||
                            from.name === "SessionEnded" ||
                            to.path === "/session-ended" ||
                            to.name === "SessionEnded") {
                            logger.debug("Navigation to/from login page, clearing force-logout flag and allowing navigation");
                            sessionStorage.removeItem("force-logout");
                            next();
                            return;
                        }
                        else if (to.matched.some((record) => record.meta.requiresAuth)) {
                            logger.debug("Auth required but user recently logged out, staying on current page");
                            next(false);
                            return;
                        }
                        // For non-auth routes, allow navigation
                        if (!to.matched.some((record) => record.meta.requiresAuth)) {
                            next();
                            return;
                        }
                    }
                }
            }
            else {
                // Remove old logout flag
                sessionStorage.removeItem("force-logout");
            }
        }
        // Check if route requires authentication
        if (to.matched.some((record) => record.meta.requiresAuth)) {
            logger.debug("Route requires authentication");
            logger.debug("Navigation from:", from.path, "to:", to.path);
            // Wait for authentication service initialization to complete
            await waitForAuthInitialization();
            logger.debug("After initialization - Auth state:", {
                isInitialized: keycloakAuthService.isInitialized(),
                isAuthenticated: keycloakAuthService.isAuthenticated(),
            });
            // Additional check: try to refresh token if we have one stored but not authenticated
            if (!keycloakAuthService.isAuthenticated() && keycloakAuthService.isInitialized()) {
                const storedToken = sessionStorage.getItem("kc-token");
                const storedRefreshToken = sessionStorage.getItem("kc-refresh-token");
                // Defensive check: both tokens must exist for a valid session
                if (storedToken && storedRefreshToken) {
                    logger.debug("Found stored tokens, attempting to validate...");
                    try {
                        // Use the service's refresh token method
                        const refreshed = await keycloakAuthService.refreshToken();
                        if (refreshed) {
                            logger.debug("Token validated successfully");
                        }
                        else {
                            logger.debug("Token refresh failed, clearing stored tokens");
                            sessionStorage.removeItem("kc-token");
                            sessionStorage.removeItem("kc-refresh-token");
                        }
                    }
                    catch (error) {
                        logger.debug("Token validation failed, clearing stored tokens:", error);
                        sessionStorage.removeItem("kc-token");
                        sessionStorage.removeItem("kc-refresh-token");
                    }
                }
                else if (storedToken || storedRefreshToken) {
                    // Defensive: if only one token exists, clear both to avoid inconsistent state
                    logger.debug("Inconsistent token state detected in router, clearing all tokens");
                    sessionStorage.removeItem("kc-token");
                    sessionStorage.removeItem("kc-refresh-token");
                }
            }
            if (!keycloakAuthService.isAuthenticated()) {
                logger.debug("User not authenticated, starting login...");
                // Save target path for redirect after login (without hash parameters)
                const cleanPath = to.path +
                    (to.query && Object.keys(to.query).length > 0
                        ? "?" + new URLSearchParams(to.query).toString()
                        : "");
                logger.debug("Saving redirect path:", cleanPath);
                sessionStorage.setItem("auth-redirect-path", cleanPath);
                // Create the full redirect URI to preserve the intended destination
                const currentUrl = window.location.origin + cleanPath;
                logger.debug("Starting login with redirect URI:", currentUrl);
                // OP-5119: see comment above the login_required branch — clear the
                // cached org so the redirect uses plain `organization` scope.
                clearOrganizationCache();
                // Direct login with the intended destination as redirectUri
                try {
                    await keycloakAuthService.login({ redirectUri: currentUrl });
                    // Login will redirect, so we don't need to call next()
                    return;
                }
                catch (error) {
                    logger.debug("Failed to trigger login:", error);
                    // Fallback: stay on current page
                    next(false);
                    return;
                }
            }
            logger.debug("User is authenticated, continuing to route:", to.path);
        }
        else {
            logger.debug("Route does not require authentication");
        }
        next(); // Continue navigation
    }
    catch (error) {
        logger.debug("Navigation guard error:", error);
        // If authentication status retrieval failed, still allow access to non-auth pages
        if (!to.matched.some((record) => record.meta.requiresAuth)) {
            next();
        }
        else {
            // For auth-required pages, try to login
            try {
                const cleanPath = to.path +
                    (to.query && Object.keys(to.query).length > 0
                        ? "?" + new URLSearchParams(to.query).toString()
                        : "");
                sessionStorage.setItem("auth-redirect-path", cleanPath);
                const currentUrl = window.location.origin + cleanPath;
                // OP-5119: clear cached org before forced login (see other branches).
                clearOrganizationCache();
                await keycloakAuthService.login({ redirectUri: currentUrl });
                return;
            }
            catch (loginError) {
                logger.debug("Failed to trigger login on error:", loginError);
                next(false);
            }
        }
    }
});
// Initialize theme before page renders to avoid flash of wrong theme
router.beforeResolve(async (to) => {
    if (keycloakAuthService.isAuthenticated() &&
        to.matched.some((record) => record.meta.requiresAuth)) {
        logger.debug("[Router] User authenticated, initializing theme preference");
        try {
            await initializeTheme();
        }
        catch (error) {
            logger.debug("[Router] Failed to initialize theme preference:", error);
        }
    }
});
// Post-route processing to handle cleanup and edge cases
router.afterEach((to) => {
    // Detect return from Keycloak password reset and show success toast
    if (to.query.passwordReset === "success") {
        const { showSuccess } = useToast();
        showSuccess(i18n.global.t("auth.resetPasswordSuccess"));
        const { passwordReset: _passwordReset, ...remainingQuery } = to.query;
        router.replace({ ...to, query: remainingQuery });
    }
    // Check if we should stay on error page
    const stayOnErrorPage = sessionStorage.getItem("stay-on-error-page");
    if (stayOnErrorPage && to.path === "/error") {
        logger.debug("Staying on error page as requested");
        sessionStorage.removeItem("stay-on-error-page");
        return;
    }
    // Clean up Keycloak URL parameters from hash if present
    const hash = window.location.hash;
    if (hash &&
        (hash.includes("error=") ||
            hash.includes("code=") ||
            hash.includes("state=") ||
            hash.includes("session_state="))) {
        logger.debug("Cleaning up Keycloak parameters from URL hash");
        const cleanPath = buildPathWithQuery(to.path, to.query);
        window.history.replaceState({}, document.title, cleanPath);
    }
    // Skip all authentication-related processing for error page
    if (to.path === "/error") {
        logger.debug("On error page - skipping all authentication processing");
        return;
    }
    // Handle redirect after successful authentication
    const redirectPath = sessionStorage.getItem("auth-redirect-path");
    if (keycloakAuthService.isAuthenticated() && redirectPath) {
        // If we're on the login page, and we have a redirect path, redirect to it
        if (to.path === "/session-ended") {
            logger.debug("Authenticated user with redirect path, redirecting to:", redirectPath);
            sessionStorage.removeItem("auth-redirect-path");
            setTimeout(() => {
                router.push(redirectPath);
            }, 100);
            return;
        }
        // If we've reached the target destination, clear the redirect path
        if (isAuthRedirectTarget(to, redirectPath)) {
            logger.debug("Successfully reached target destination, clearing redirect path");
            sessionStorage.removeItem("auth-redirect-path");
        }
    }
    // Handle special case: authenticated user on login page (without redirect) should go to dashboard
    if (to.path === "/session-ended" && keycloakAuthService.isAuthenticated() && !redirectPath) {
        logger.debug("Authenticated user on login page without redirect, going to dashboard");
        setTimeout(() => {
            router.push("/dashboard");
        }, 100);
        return;
    }
    // Capture PostHog BO pageview events (FO tracking is handled by the payment router)
    if (isBackOfficeRoute(to) && postHogBOService.isInitialized()) {
        try {
            postHogBOService.capture("$pageview", {
                $current_url: window.location.href,
                path: to.path,
                name: to.name,
            });
        }
        catch (error) {
            logger.debug("Failed to capture pageview in PostHog Back Office:", error);
        }
    }
    logger.debug("Navigated to:", to.path);
});
export default router;
